/**
 * Reading the parts of a Vancouver or AMA journal reference.
 *
 * Both styles come from the same biomedical tradition and share their skeleton:
 *
 *     Halpern SD, Ubel PA, Caplan AL. Solid-organ transplantation in HIV-infected
 *     patients. N Engl J Med. 2002 Jul 25;347(4):284-7.
 *
 * — surname then initials with no periods or spaces, names separated by commas, a
 * sentence-case title with no quotation marks, an NLM journal abbreviation with no
 * periods, then `Year;Volume(Issue):Pages`. They differ in the details the checkers in
 * `src/lib/citation-check-vancouver.ts` and `src/lib/citation-check-ama.ts` test: et al.
 * thresholds, page ranges, months and the DOI's punctuation.
 *
 * The author parser reads the names one at a time and records the FORM each was written
 * in, so a checker can say exactly what is off ("Smith J. A." has periods; "Smith, JA"
 * has a comma; "J. A. Smith" is in natural order). When the start of an entry does not
 * read as a list of personal names — a group author, an organization — it returns null
 * and the checkers say nothing about authors at all: a missed issue is better than a
 * wrong one.
 */

export type NameForm = "standard" | "periods" | "comma" | "natural";

export interface ParsedName {
  raw: string;
  form: NameForm;
  surname: string;
  /** Initials as bare capitals: "SD". */
  initials: string;
  suffix: string;
}

export type AuthorSeparator =
  | "comma"
  | "and"
  | "comma-and"
  | "amp"
  | "semicolon";

export interface AuthorList {
  names: ParsedName[];
  separators: AuthorSeparator[];
  etAl: boolean;
  /** A group author after a semicolon: "…; Alf-One Study Group." */
  group: string | null;
  /** Offset just past the author element's closing period. */
  end: number;
}

const PARTICLE =
  "(?:(?:van|von|de|der|den|da|di|du|la|le|del|della|dos|das|ter|ten|bin|al|el)\\s)*";
const SURNAME_WORD = "\\p{Lu}[\\p{Ll}'’][\\p{L}'’-]*";
const SURNAME = `${PARTICLE}${SURNAME_WORD}(?:\\s${SURNAME_WORD})?`;
const SUFFIX = "(?:\\s(Jr|Sr|2nd|3rd|4th|II|III|IV)\\.?)?";

const RE_SURNAME = new RegExp(SURNAME, "uy");
/** "SD" — initials as Vancouver and AMA write them: capitals, no periods or spaces. */
const RE_PLAIN_INITIALS = /\s(\p{Lu}{1,4})(?![\p{L}.])/uy;
/** "SD." where the period closes the author element rather than an initial. */
const RE_PLAIN_THEN_STOP = /\s(\p{Lu}{1,4})(?=\.(?:\s|$))/uy;
/** "S. D." / "S.D." / "S." followed by a comma: initials with periods. */
const RE_DOTTED_INITIALS =
  /\s((?:\p{Lu}\.\s?-?){2,4}|\p{Lu}\.(?=,|\s+(?:and|&)\s))/uy;
/** ", S. D." / ", SD" — the APA/Harvard comma after the surname. */
const RE_COMMA_INITIALS = /,\s((?:\p{Lu}\.?\s?-?){1,4})(?=[,;]|\.?(?:\s|$))/uy;
const RE_SUFFIX = new RegExp(SUFFIX, "uy");
const RE_NATURAL = new RegExp(
  `((?:\\p{Lu}\\.\\s?-?){1,3})\\s?(${SURNAME})`,
  "uy"
);
const RE_ETAL = /,?\s*et\.?\s?al\b\.?/y;
const RE_SEPARATOR = /(,\s*and\s+|,\s*&\s*|\s+and\s+|\s*&\s*|;\s*|,\s*)/y;
const RE_GROUP = /[^.]+\./y;
const RE_NOT_CAPITAL = /[^\p{Lu}]/gu;
const RE_TRAILING_SPACE = /\s+$/;

function letters(initials: string): string {
  return initials.replace(RE_NOT_CAPITAL, "");
}

function at(re: RegExp, text: string, pos: number): RegExpExecArray | null {
  re.lastIndex = pos;
  return re.exec(text);
}

function readSuffix(
  text: string,
  pos: number
): { suffix: string; end: number } {
  const match = at(RE_SUFFIX, text, pos);
  if (!match || match[0] === "") {
    return { suffix: "", end: pos };
  }
  return { suffix: match[1] ?? "", end: pos + match[0].length };
}

function surnameFirst(text: string, pos: number): ParsedName | null {
  const surname = at(RE_SURNAME, text, pos);
  if (!surname) {
    return null;
  }
  const after = pos + surname[0].length;
  const attempts: [RegExp, NameForm][] = [
    [RE_DOTTED_INITIALS, "periods"],
    [RE_COMMA_INITIALS, "comma"],
    [RE_PLAIN_INITIALS, "standard"],
    [RE_PLAIN_THEN_STOP, "standard"],
  ];
  for (const [re, form] of attempts) {
    const match = at(re, text, after);
    if (!match) {
      continue;
    }
    const initials = letters(match[1]);
    if (initials === "") {
      continue;
    }
    const tail = readSuffix(text, after + match[0].length);
    return {
      raw: text.slice(pos, tail.end).replace(RE_TRAILING_SPACE, ""),
      form,
      surname: surname[0],
      initials,
      suffix: tail.suffix,
    };
  }
  return null;
}

function naturalOrder(text: string, pos: number): ParsedName | null {
  const match = at(RE_NATURAL, text, pos);
  if (!match) {
    return null;
  }
  const tail = readSuffix(text, pos + match[0].length);
  return {
    raw: text.slice(pos, tail.end),
    form: "natural",
    surname: match[2],
    initials: letters(match[1]),
    suffix: tail.suffix,
  };
}

/** One personal name at `pos`, in whichever form it was written. */
export function matchName(text: string, pos: number): ParsedName | null {
  return surnameFirst(text, pos) ?? naturalOrder(text, pos);
}

function separatorKind(raw: string): AuthorSeparator {
  if (raw.includes("&")) {
    return "amp";
  }
  if (raw.startsWith(";")) {
    return "semicolon";
  }
  if (raw.includes("and")) {
    return raw.includes(",") ? "comma-and" : "and";
  }
  return "comma";
}

/** The author element ends here: a period, or the period an initial already carried. */
function closes(
  text: string,
  pos: number,
  previous: ParsedName
): number | null {
  if (text[pos] === ".") {
    return pos + 1;
  }
  if (previous.raw.endsWith(".") && (pos >= text.length || text[pos] === " ")) {
    return pos;
  }
  return null;
}

interface ParseState {
  names: ParsedName[];
  separators: AuthorSeparator[];
  pos: number;
}

/** After a name: et al., a separator and another name, a group author, or the end. */
function step(
  text: string,
  state: ParseState,
  name: ParsedName
): AuthorList | "continue" | null {
  const etAl = at(RE_ETAL, text, state.pos);
  if (etAl) {
    const end = state.pos + etAl[0].length;
    const close = etAl[0].endsWith(".") ? end : closes(text, end, name);
    return close === null
      ? null
      : { ...state, etAl: true, group: null, end: close };
  }
  const separator = at(RE_SEPARATOR, text, state.pos);
  if (separator) {
    const next = state.pos + separator[0].length;
    const kind = separatorKind(separator[0]);
    if (kind === "semicolon" && !matchName(text, next)) {
      const group = at(RE_GROUP, text, next);
      return group
        ? {
            ...state,
            etAl: false,
            group: group[0].slice(0, -1).trim(),
            end: next + group[0].length,
          }
        : null;
    }
    if (matchName(text, next)) {
      state.separators.push(kind);
      state.pos = next;
      return "continue";
    }
  }
  const close = closes(text, state.pos, name);
  return close === null
    ? null
    : { ...state, etAl: false, group: null, end: close };
}

/**
 * The personal names at the start of an entry, or null when the entry does not open
 * with a readable list of them (a group author, an organization, a title).
 */
export function parseAuthors(text: string): AuthorList | null {
  const state: ParseState = { names: [], separators: [], pos: 0 };
  for (let guard = 0; guard < 100; guard++) {
    const name = matchName(text, state.pos);
    if (!name) {
      return null;
    }
    state.names.push(name);
    state.pos += name.raw.length;
    const outcome = step(text, state, name);
    if (outcome !== "continue") {
      return outcome;
    }
  }
  return null;
}

/** "Smith JA" — a name in Vancouver/AMA form. */
export function standardName(name: ParsedName): string {
  const base = `${name.surname} ${name.initials}`;
  return name.suffix ? `${base} ${name.suffix}` : base;
}

/** The corrected author element: names, then ", et al." when cut short. */
export function standardAuthorList(
  names: readonly ParsedName[],
  keep: number,
  etAl: boolean
): string {
  const shown = names.slice(0, keep).map(standardName).join(", ");
  return etAl ? `${shown}, et al.` : `${shown}.`;
}

// --- title, journal, locator -----------------------------------------------------

const RE_OPEN_QUOTE = /^["“‘']/;
const RE_TITLE_END = /[.?!](?=\s|$)/;
const RE_LEADING_SPACE = /^\s+/;
const RE_TITLE_TRAILING_PUNCT = /[.,]$/;

export interface TitlePart {
  title: string;
  quoted: boolean;
  /** Offset (in the entry) just past the title's closing punctuation. */
  end: number;
}

const CLOSING: Record<string, string> = {
  '"': '"',
  "“": "”",
  "‘": "’",
  "'": "'",
};

/** The title after the author element: quoted, or up to its closing period. */
export function readTitle(text: string, from: number): TitlePart | null {
  const lead = RE_LEADING_SPACE.exec(text.slice(from))?.[0].length ?? 0;
  const start = from + lead;
  const rest = text.slice(start);
  if (rest === "") {
    return null;
  }
  if (RE_OPEN_QUOTE.test(rest)) {
    const close = rest.indexOf(CLOSING[rest[0]] ?? rest[0], 1);
    if (close > 1) {
      // The title ends at its closing quote, or just after a period or comma that
      // follows it ("Title". / "Title",) — never at the next sentence break, which
      // would be inside the journal abbreviation.
      const trailing = rest[close + 1] === "." || rest[close + 1] === ",";
      return {
        title: rest.slice(1, close).replace(RE_TITLE_TRAILING_PUNCT, ""),
        quoted: true,
        end: start + close + 1 + (trailing ? 1 : 0),
      };
    }
  }
  const stop = RE_TITLE_END.exec(rest);
  if (!stop) {
    return null;
  }
  return {
    title: rest.slice(0, stop.index),
    quoted: false,
    end: start + stop.index + 1,
  };
}

export interface Locator {
  index: number;
  raw: string;
  year: string;
  month: string | null;
  day: string | null;
  volume: string | null;
  issue: string | null;
  first: string | null;
  last: string | null;
  /** Spaces around ";" or ":", or before "(": "2019; 321(5): 456-460". */
  spaced: boolean;
  /** The same block with the spacing closed up. */
  compact: string;
}

const RE_LOCATOR =
  /\b(\d{4})(?:\s+([A-Za-z]{3,9}\.?)(?:\s+(\d{1,2}))?)?(?:\s*\[[^\]]*\])?(\s*;\s*)([\dA-Za-z]+(?:[-–][\dA-Za-z]+)?)?(?:(\s*)\(([^)]*)\))?(?:(\s*:\s*)([A-Za-z]?\d+[A-Za-z]?)(?:\s*[-–—]\s*([A-Za-z]?\d+[A-Za-z]?))?)?/;

/** `Year[ Mon[ Day]];Volume(Issue):Pages` — the biomedical locator block. */
export function findLocator(text: string): Locator | null {
  const match = RE_LOCATOR.exec(text);
  if (!match) {
    return null;
  }
  const [
    ,
    year,
    month,
    day,
    semi,
    volume,
    parenSpace,
    issue,
    colon,
    first,
    last,
  ] = match;
  const spaced =
    semi.trim() !== semi ||
    Boolean(parenSpace) ||
    (colon !== undefined && colon.trim() !== colon);
  const date = [year, month, day].filter(Boolean).join(" ");
  let compact = `${date};${volume ?? ""}`;
  if (issue !== undefined) {
    compact += `(${issue})`;
  }
  if (first !== undefined) {
    compact += `:${first}${last ? `-${last}` : ""}`;
  }
  return {
    index: match.index,
    raw: match[0],
    year,
    month: month ?? null,
    day: day ?? null,
    volume: volume ?? null,
    issue: issue ?? null,
    first: first ?? null,
    last: last ?? null,
    spaced,
    compact,
  };
}

const RE_DOTTED_WORD = /(\p{L})\.(?=\s)/gu;
const RE_DOTTED_RUN = /(\p{Lu})\.(?=\p{Lu})/gu;
const RE_ABBREVIATED_WORD = /(?:^|\s)\p{Lu}\p{Ll}{0,9}\.(?=\s)/gu;

/**
 * "N. Engl. J. Med." → "N Engl J Med.", "Arch. Ophthalmol." → "Arch Ophthalmol." and
 * "Proc. Natl. Acad. Sci. U.S.A." → "Proc Natl Acad Sci U S A." — NLM abbreviations
 * carry no periods. Null when no capitalized word before the last one ends in a
 * period ("Lancet.", "N Engl J Med.").
 */
export function undottedJournal(journal: string): string | null {
  const trimmed = journal.trim();
  const inner = trimmed.endsWith(".") ? trimmed.slice(0, -1) : trimmed;
  const dotted = inner.match(RE_ABBREVIATED_WORD)?.length ?? 0;
  if (dotted < 1) {
    return null;
  }
  return `${inner.replace(RE_DOTTED_WORD, "$1").replace(RE_DOTTED_RUN, "$1 ")}.`;
}

/** How the names of an author list depart from "Surname AB", worst first. */
export type NameFormIssue = "natural" | "comma" | "periods";

export function nameFormIssue(list: AuthorList): NameFormIssue | null {
  const forms = new Set(list.names.map((name) => name.form));
  for (const issue of ["natural", "comma", "periods"] as const) {
    if (forms.has(issue)) {
      return issue;
    }
  }
  return null;
}

/** "and" or "&" before a name — neither biomedical style uses one. */
export function usesConjunction(list: AuthorList): boolean {
  return list.separators.some(
    (separator) =>
      separator === "and" || separator === "comma-and" || separator === "amp"
  );
}

/**
 * The corrected author element, keeping a group author that follows the names:
 * "Smith JA, Doe B; Alf-One Study Group."
 */
export function correctedAuthors(
  list: AuthorList,
  keep: number,
  etAl: boolean
): string {
  const names = standardAuthorList(list.names, keep, etAl);
  return list.group ? `${names.slice(0, -1)}; ${list.group}.` : names;
}

/** The text between the end of the title and the locator: the journal title. */
export function journalSegment(
  text: string,
  title: TitlePart,
  locator: Locator
): string | null {
  if (locator.index <= title.end) {
    return null;
  }
  const segment = text.slice(title.end, locator.index).trim();
  return segment === "" ? null : segment;
}

/** Text that looks like IEEE's locators — "vol.", "no.", "pp." — in a biomedical entry. */
export const RE_IEEE_LOCATORS = /\b(?:vol|no|pp)\.\s*\d/i;

export interface MonthFix {
  found: string;
  expected: string;
}

const MONTHS: readonly [string, string][] = [
  ["january", "Jan"],
  ["february", "Feb"],
  ["march", "Mar"],
  ["april", "Apr"],
  ["may", "May"],
  ["june", "Jun"],
  ["july", "Jul"],
  ["august", "Aug"],
  ["september", "Sep"],
  ["sept", "Sep"],
  ["october", "Oct"],
  ["november", "Nov"],
  ["december", "Dec"],
];

/** Citing Medicine abbreviates months to three letters, no period: "Jul", not "July" or "Jul.". */
export function nlmMonth(month: string): MonthFix | null {
  const bare = month.replace(".", "").toLowerCase();
  const full = MONTHS.find(([name]) => name === bare);
  const short = MONTHS.find(([, abbr]) => abbr.toLowerCase() === bare);
  const expected = full?.[1] ?? short?.[1];
  if (!expected || month === expected) {
    return null;
  }
  return { found: month, expected };
}
